import api from './api';
import type { HomepageContent, HomepageContentFormData, HomepageStatistics, ApiResponse } from '../../types/homepage-content.types';

class HomepageContentService {
  private readonly endpoint = '/api/homepage-content';

  async getAll(contentType?: string): Promise<ApiResponse<HomepageContent[]>> {
    const params: any = { include_inactive: 'true' };
    if (contentType) params.type = contentType;
    const response = await api.get<ApiResponse<HomepageContent[]>>(this.endpoint, { params });
    return response.data;
  }

  async getCarouselBanners(): Promise<ApiResponse<HomepageContent[]>> {
    const response = await api.get<ApiResponse<HomepageContent[]>>(`${this.endpoint}/banners`);
    return response.data;
  }

  async getSidebarImages(): Promise<ApiResponse<HomepageContent[]>> {
    const response = await api.get<ApiResponse<HomepageContent[]>>(`${this.endpoint}/sidebar-images`);
    return response.data;
  }

  async getWelcomeContent(): Promise<ApiResponse<HomepageContent[]>> {
    const response = await api.get<ApiResponse<HomepageContent[]>>(`${this.endpoint}/welcome`);
    return response.data;
  }

  async getVisionContent(): Promise<ApiResponse<HomepageContent[]>> {
    const response = await api.get<ApiResponse<HomepageContent[]>>(`${this.endpoint}/vision`);
    return response.data;
  }

  async getObjectives(): Promise<ApiResponse<HomepageContent[]>> {
    const response = await api.get<ApiResponse<HomepageContent[]>>(`${this.endpoint}/objectives`);
    return response.data;
  }

  async getPortalLogos(): Promise<ApiResponse<HomepageContent[]>> {
    const response = await api.get<ApiResponse<HomepageContent[]>>(`${this.endpoint}/portals`);
    return response.data;
  }

  async getHeaderLogos(): Promise<ApiResponse<HomepageContent[]>> {
    const response = await api.get<ApiResponse<HomepageContent[]>>(`${this.endpoint}/header-logos`);
    return response.data;
  }

  async getHeaderLeaders(): Promise<ApiResponse<HomepageContent[]>> {
    const response = await api.get<ApiResponse<HomepageContent[]>>(`${this.endpoint}/header-leaders`);
    return response.data;
  }

  async getStatistics(): Promise<ApiResponse<HomepageStatistics>> {
    const response = await api.get<ApiResponse<HomepageStatistics>>(`${this.endpoint}/statistics`);
    return response.data;
  }

  async getById(id: number): Promise<ApiResponse<HomepageContent>> {
    const response = await api.get<ApiResponse<HomepageContent>>(`${this.endpoint}/${id}`);
    return response.data;
  }

  async create(contentData: HomepageContentFormData): Promise<ApiResponse<HomepageContent>> {
    const response = await api.post<ApiResponse<HomepageContent>>(this.endpoint, contentData);
    return response.data;
  }

  async update(id: number, contentData: Partial<HomepageContentFormData>): Promise<ApiResponse<HomepageContent>> {
    const response = await api.put<ApiResponse<HomepageContent>>(`${this.endpoint}/${id}`, contentData);
    return response.data;
  }

  async delete(id: number): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${this.endpoint}/${id}/hard`);
    return response.data;
  }

  async toggleStatus(id: number): Promise<ApiResponse<HomepageContent>> {
    const response = await api.put<ApiResponse<HomepageContent>>(`${this.endpoint}/${id}/toggle-status`);
    return response.data;
  }

  async uploadImage(file: File): Promise<ApiResponse<{ filename: string; url: string; size: number; mimetype: string }>> {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post<ApiResponse<{ filename: string; url: string; size: number; mimetype: string }>>(
      `${this.endpoint}/upload-image`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }
}

export const homepageContentService = new HomepageContentService();